import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Button, ButtonText, Center, Text, VStack,
} from '@gluestack-ui/themed';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

import type { IdCaptureMode } from '../preflight/IdCaptureGuideOverlays';
import { IdVerificationStep } from '../preflight/IdVerificationStep';
import { IdentityPhotoReviewStep } from '../preflight/IdentityPhotoReviewStep';
import type { LobbyStepProps } from './types';

type IdentityDraftPhase = 'document' | 'face' | 'check';

function backendAwaiting(status: string | undefined): boolean {
  return status === 'submitted';
}

/**
 * Lobby step ④ — document and face capture (local draft), check, then one pair send.
 */
export function LobbyIdentityStep({ runState, actions }: LobbyStepProps) {
  const { t } = useTranslation();
  const [isRetaking, setIsRetaking] = useState(false);
  const [capturePhase, setCapturePhase] = useState<IdentityDraftPhase>('document');
  const [documentImage, setDocumentImage] = useState<string | null>(null);
  const [faceImage, setFaceImage] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const sendInFlightRef = useRef(false);

  const { idVerification } = runState;
  const status = idVerification?.status;
  const isRejected = status === 'rejected' && !isRetaking;

  useEffect(() => {
    if (isRetaking) {
      setCapturePhase('document');
      setDocumentImage(null);
      setFaceImage(null);
      setSendError(null);
    }
  }, [isRetaking]);

  useEffect(() => {
    if (backendAwaiting(status) && isRetaking) {
      setIsRetaking(false);
    }
  }, [status, isRetaking]);

  if (isRejected) {
    const { rejectionReasonCode, rejectionReason } = idVerification ?? {};

    let reasonLabel: string;
    if (rejectionReasonCode) {
      reasonLabel = t(`oralExam.idReject.${rejectionReasonCode}`, {
        defaultValue: t('oralExam.idReject.fallback'),
      });
    } else {
      reasonLabel = rejectionReason ?? t('oralExam.idReject.fallback');
    }

    const note = rejectionReasonCode ? rejectionReason : null;

    return (
      <Center flex={1} p="$6" testID="oral-exam-id-rejected">
        <VStack space="md" maxWidth={560} width="100%" alignItems="center">
          <Box
            w={72}
            h={72}
            borderRadius="$full"
            bg="$statusErrorSubtle"
            borderWidth={2}
            borderColor="$statusError"
            alignItems="center"
            justifyContent="center"
          >
            <Box w={36} h={36} borderRadius="$full" bg="$statusError" opacity={0.85} />
          </Box>

          <Text size="xl" fontWeight="semibold" color="$statusError" textAlign="center">
            {reasonLabel}
          </Text>

          {!!note && (
            <Text size="md" color="$textSubtle" textAlign="center">
              {note}
            </Text>
          )}

          <Text size="md" color="$textBody" textAlign="center">
            {t('oralExam.idReject.retakePrompt')}
          </Text>

          <Button
            size="md"
            onPress={() => setIsRetaking(true)}
            testID="oral-exam-id-retake-button"
            width="100%"
            maxWidth={280}
          >
            <ButtonText>{t('oralExam.idReject.retakeButton')}</ButtonText>
          </Button>
        </VStack>
      </Center>
    );
  }

  if (capturePhase === 'check' && documentImage && faceImage) {
    const handleSend = async () => {
      if (sendInFlightRef.current) return;
      sendInFlightRef.current = true;
      setSendError(null);
      try {
        await actions.submitIdentityPhotos(documentImage, faceImage);
      } catch (error) {
        const statusCode = error instanceof AxiosError ? error.response?.status : undefined;
        if (statusCode === 409) {
          setSendError(t('oralExam.idCapture.review.alreadySent'));
        } else {
          setSendError(t('oralExam.idCapture.review.sendFailed'));
        }
      } finally {
        sendInFlightRef.current = false;
      }
    };

    return (
      <IdentityPhotoReviewStep
        documentImage={documentImage}
        faceImage={faceImage}
        onSend={handleSend}
        onRetakeDocument={() => {
          setSendError(null);
          setCapturePhase('document');
        }}
        onRetakeFace={() => {
          setSendError(null);
          setCapturePhase('face');
        }}
        isSending={actions.isSubmittingIdentityPhotos}
        sendError={sendError}
      />
    );
  }

  const mode: IdCaptureMode = capturePhase === 'face' ? 'selfie' : 'document';

  return (
    <IdVerificationStep
      key={mode}
      mode={mode}
      onCapture={(imageBase64) => {
        if (!imageBase64?.trim()) return;
        if (mode === 'document') {
          setDocumentImage(imageBase64);
          setCapturePhase(faceImage ? 'check' : 'face');
        } else {
          setFaceImage(imageBase64);
          setCapturePhase('check');
        }
      }}
      submitted={false}
    />
  );
}
